import { memo, useEffect } from 'react';

import { DropdownMenuSeparator } from '@air/primitive-dropdown-menu';
import { formatBytes } from '@air/utilities';

import { StorageUpgradeCTA } from '~/components/Upsells/StorageUpgradeCTA';
import { TrialCTA } from '~/components/Upsells/TrialCTA';
import { Meter } from '~/components/Workspace/Plan/components/Usage/components/Meter';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useShouldShowStorageUsage } from '~/swr-hooks/subscriptions/useShouldShowStorageUsage';
import { useStorage } from '~/swr-hooks/subscriptions/useStorage';
import { isSubscriptionOnTrial, shouldShowStorageUpgrade } from '~/utils/SubscriptionUtils';
import { getUsedStoragePercentage } from '~/utils/WorkspaceUtils';

export const WorkspaceHeaderInformationDropdownStorageItems = memo(() => {
  const { maxStorageBytes } = useShouldShowStorageUsage();
  const { data: storage, refetch } = useStorage();
  const { data: subscription } = useSubscriptionContext();
  const isOnTrial = isSubscriptionOnTrial(subscription);
  const showStorageUpgrade = shouldShowStorageUpgrade({ subscription, storage });

  /**
   * Whenever they open this popover, we want to refetch the storage data
   * to make sure we have the latest info but we don't want to do update the
   * stale time cause we don't want other places to fetch it
   *
   * @see https://air-labs-team.slack.com/archives/C018C7U0DQU/p1690572803551579
   */
  useEffect(() => {
    refetch();
  }, [refetch]);

  if (!maxStorageBytes || !storage) {
    return null;
  }

  const percentOfStorageUsed = getUsedStoragePercentage(storage.usedStorage, maxStorageBytes);

  return (
    <>
      <div className="flex flex-col gap-2 px-3 py-2">
        <p
          className="font-semibold ui2026:font-medium ads:text-secondary text-10 text-gray-10 ui2026:text-12 ui2026:text-gray-11 uppercase ui2026:normal-case tracking-wider ui2026:tracking-normal"
          data-testid="WORKSPACE_HEADER_STORAGE_LABEL"
        >
          Workspace Storage
        </p>
        <Meter
          hasInfo
          isOutOfStorage={storage.isOutOfStorage}
          maxBytes={formatBytes(maxStorageBytes)}
          usedBytes={storage.formattedUsedBytes}
          usedStoragePercent={percentOfStorageUsed}
        />
        {isOnTrial ? <TrialCTA /> : showStorageUpgrade && <StorageUpgradeCTA />}
      </div>
      <DropdownMenuSeparator />
    </>
  );
});

WorkspaceHeaderInformationDropdownStorageItems.displayName = 'WorkspaceHeaderInformationDropdownStorageItems';
